import { withDebounce } from 'components/WorkspaceEditor/utils'
import { useEffect, useRef, useState } from 'react'

// Constants
/**
 * Offset for dropdown content width calculation accounting for padding, margins, and borders
 * This ensures the text width calculation excludes the dropdown's internal spacing
 */
const DROPDOWN_CONTENT_OFFSET = 30

/**
 * Custom hook that measures the width of a version dropdown element for text width calculation.
 * Includes memory leak prevention and proper cleanup of ResizeObserver.
 *
 * @template T The type of the dropdown element.
 * @returns An object containing the text width and a ref to the dropdown element.
 */
export const useVersionDropdownResizeObserver = <
    T extends HTMLElement = HTMLDivElement
>() => {
    const versionDropdownRef = useRef<T | null>(null)
    const [textWidth, setTextWidth] = useState(0)

    useEffect(() => {
        // Set mounted flag
        let isMounted = true
        let rafId: number | null = null

        let resizeObserver: ResizeObserver | null = null
        const handleResize = withDebounce<ResizeObserverCallback>(
            (observer) => {
                if (isMounted && observer?.[0]) {
                    const { width } = observer[0].contentRect
                    // Use requestAnimationFrame to defer state update to next frame
                    // This prevents "ResizeObserver loop completed with undelivered notifications" error
                    // by allowing the browser to complete its layout pass before React re-renders
                    if (rafId !== null) {
                        cancelAnimationFrame(rafId)
                    }
                    rafId = requestAnimationFrame(() => {
                        if (isMounted) {
                            // Subtract padding/margin for text width calculation
                            setTextWidth(width - DROPDOWN_CONTENT_OFFSET)
                        }
                        rafId = null
                    })
                }
            }
        )

        try {
            resizeObserver = new ResizeObserver(handleResize)

            if (versionDropdownRef.current) {
                resizeObserver.observe(versionDropdownRef.current)
            }
        } catch (error) {
            console.error('ResizeObserver error:', error)
        }

        return () => {
            // Mark component as unmounted
            isMounted = false

            // Cancel any pending animation frame to prevent memory leaks
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }

            if (resizeObserver) {
                resizeObserver.disconnect()
                handleResize.cancel()
            }
        }
    }, [])

    return {
        versionDropdownRef,
        textWidth,
    }
}
